import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { User, Building2, KeyRound, ShieldCheck, Save, Eye, EyeOff, Mail, Send, FileText } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ProfilePage() {
  const { user, refreshUser } = useAuth();

  // Profile form
  const [profile, setProfile] = useState({ name: '', email: '', phone: '', company_name: '' });
  const [savingProfile, setSavingProfile] = useState(false);

  // Password form
  const [pwd, setPwd] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [changingPwd, setChangingPwd] = useState(false);

  // Daily Fleet Health digest opt-in
  const [digestEnabled, setDigestEnabled] = useState(false);
  const [digestLoading, setDigestLoading] = useState(true);
  const [sendingTest, setSendingTest] = useState(false);
  const [previewingDigest, setPreviewingDigest] = useState(false);

  const isMainUser = user?.role === 'main_user';

  useEffect(() => {
    if (user) {
      setProfile({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        company_name: user.company_name || '',
      });
    }
  }, [user]);

  // Load current digest opt-in state on mount
  useEffect(() => {
    axios.get(`${API_URL}/api/settings/email-report`)
      .then(r => setDigestEnabled(!!r.data?.enabled))
      .catch(() => setDigestEnabled(false))
      .finally(() => setDigestLoading(false));
  }, []);

  const toggleDigest = async () => {
    const next = !digestEnabled;
    setDigestEnabled(next); // optimistic
    try {
      const r = await axios.post(`${API_URL}/api/settings/email-report`, { enabled: next });
      toast.success(r.data.message);
    } catch (e) {
      setDigestEnabled(!next);
      toast.error(e.response?.data?.detail || 'Failed to save preference');
    }
  };

  const sendTestDigest = async () => {
    setSendingTest(true);
    try {
      const r = await axios.post(`${API_URL}/api/settings/email-report/test`);
      toast.success(r.data.message || 'Test digest sent');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send test digest');
    } finally {
      setSendingTest(false);
    }
  };

  const previewDigest = async () => {
    // Fetch the PDF as a blob and open it in a new tab. Avoids emailing.
    setPreviewingDigest(true);
    try {
      const r = await axios.get(`${API_URL}/api/settings/email-report/preview`, {
        responseType: 'blob',
      });
      const blobUrl = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const win = window.open(blobUrl, '_blank', 'noopener');
      if (!win) {
        // Pop-up blocked — fall back to download
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = 'fleet_digest_preview.pdf';
        a.click();
      }
      // Revoke the URL after a short delay so the new tab has a chance to render
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to preview digest');
    } finally {
      setPreviewingDigest(false);
    }
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    if (!profile.name.trim()) {
      toast.error('Name is required');
      return;
    }
    if (!profile.email.trim()) {
      toast.error('Email is required');
      return;
    }
    setSavingProfile(true);
    try {
      const payload = {
        name: profile.name.trim(),
        email: profile.email.trim().toLowerCase(),
        phone: profile.phone.trim() || null,
      };
      if (isMainUser) payload.company_name = profile.company_name.trim() || null;

      await axios.put(`${API_URL}/api/auth/me`, payload);
      toast.success('Profile updated');
      if (refreshUser) await refreshUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (!pwd.current_password || !pwd.new_password) {
      toast.error('Please fill in both password fields');
      return;
    }
    if (pwd.new_password.length < 8) {
      toast.error('New password must be at least 8 characters');
      return;
    }
    if (pwd.new_password !== pwd.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }
    setChangingPwd(true);
    try {
      await axios.post(`${API_URL}/api/auth/change-password`, {
        current_password: pwd.current_password,
        new_password: pwd.new_password,
      });
      toast.success('Password updated');
      setPwd({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPwd(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-6" data-testid="profile-page">
      <div>
        <h1 className="font-heading text-3xl font-bold text-slate-900">Account Settings</h1>
        <p className="text-slate-500 mt-1">Update your personal info, company details, and password.</p>
      </div>

      {/* Personal Info */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="w-5 h-5" />
            Personal Information
          </CardTitle>
          <CardDescription>Your name, email, and contact number.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={saveProfile} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="profile-name">Full Name</Label>
                <Input
                  id="profile-name"
                  value={profile.name}
                  onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                  placeholder="Your name"
                  data-testid="profile-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="profile-email">Email</Label>
                <Input
                  id="profile-email"
                  type="email"
                  value={profile.email}
                  onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                  placeholder="you@company.co.za"
                  data-testid="profile-email-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="profile-phone">Phone (optional)</Label>
              <Input
                id="profile-phone"
                type="tel"
                value={profile.phone}
                onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                placeholder="+27 82 123 4567"
                data-testid="profile-phone-input"
              />
            </div>

            {isMainUser && (
              <div className="pt-4 border-t space-y-2">
                <Label htmlFor="profile-company" className="flex items-center gap-2">
                  <Building2 className="w-4 h-4" />
                  Company Name
                  <Badge variant="outline" className="text-[10px] font-mono ml-1">Main user only</Badge>
                </Label>
                <Input
                  id="profile-company"
                  value={profile.company_name}
                  onChange={(e) => setProfile({ ...profile, company_name: e.target.value })}
                  placeholder="Your fleet business name"
                  data-testid="profile-company-input"
                />
                <p className="text-xs text-slate-500">This appears on invoices, reports, and team invite emails.</p>
              </div>
            )}

            <div className="flex justify-end pt-2">
              <Button type="submit" disabled={savingProfile} data-testid="save-profile-btn">
                <Save className="w-4 h-4 mr-2" />
                {savingProfile ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Role/Access display */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5" />
            Account Type
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-1">Role</p>
              <Badge className={isMainUser ? 'bg-slate-900 text-white' : 'bg-blue-100 text-blue-700'}>
                {isMainUser ? 'Main Account Owner' : 'Team Member'}
              </Badge>
            </div>
            {!isMainUser && user?.access_level && (
              <div>
                <p className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-1">Access Level</p>
                <Badge variant="outline">{user.access_level.replace('_', ' ')}</Badge>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Change Password */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <KeyRound className="w-5 h-5" />
            Change Password
          </CardTitle>
          <CardDescription>Update your login password. Requires your current password.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={changePassword} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="current-password">Current Password</Label>
              <div className="relative">
                <Input
                  id="current-password"
                  type={showCurrent ? 'text' : 'password'}
                  value={pwd.current_password}
                  onChange={(e) => setPwd({ ...pwd, current_password: e.target.value })}
                  autoComplete="current-password"
                  data-testid="current-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrent((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-900"
                  aria-label="Toggle current password visibility"
                >
                  {showCurrent ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="new-password">New Password</Label>
                <div className="relative">
                  <Input
                    id="new-password"
                    type={showNew ? 'text' : 'password'}
                    value={pwd.new_password}
                    onChange={(e) => setPwd({ ...pwd, new_password: e.target.value })}
                    autoComplete="new-password"
                    data-testid="new-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNew((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-900"
                    aria-label="Toggle new password visibility"
                  >
                    {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-xs text-slate-500">At least 8 characters</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm-password">Confirm New Password</Label>
                <Input
                  id="confirm-password"
                  type={showNew ? 'text' : 'password'}
                  value={pwd.confirm_password}
                  onChange={(e) => setPwd({ ...pwd, confirm_password: e.target.value })}
                  autoComplete="new-password"
                  data-testid="confirm-password-input"
                />
              </div>
            </div>
            <div className="flex justify-end pt-2">
              <Button type="submit" disabled={changingPwd} data-testid="change-password-btn">
                <KeyRound className="w-4 h-4 mr-2" />
                {changingPwd ? 'Updating...' : 'Update Password'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Daily Fleet Health Digest — main-user only. Team members' digest is managed by their admin on the Team page. */}
      {isMainUser && (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-obsidian" /> Daily Fleet Health Digest
          </CardTitle>
          <CardDescription>
            Get one morning email with yesterday&apos;s spend, compliance alerts, incidents, fuel efficiency drops, and upcoming milestones.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={digestEnabled}
              onChange={toggleDigest}
              disabled={digestLoading}
              className="mt-1 h-4 w-4 accent-obsidian"
              data-testid="digest-opt-in-toggle"
            />
            <div className="flex-1">
              <span className="text-sm font-medium text-slate-900">Send me the daily digest at 06:00 SAST</span>
              <p className="text-xs text-slate-500 mt-0.5">
                Delivered to <span className="font-medium">{user?.email}</span>. Turn off any time.
              </p>
            </div>
          </label>
          <div className="flex items-center gap-3 pt-2 border-t border-slate-100 flex-wrap">
            <Button
              type="button" variant="outline" size="sm"
              onClick={previewDigest}
              disabled={previewingDigest}
              data-testid="digest-preview-btn"
            >
              <FileText className="w-4 h-4 mr-2" />
              {previewingDigest ? 'Building…' : 'Preview Today\u2019s Digest'}
            </Button>
            <Button
              type="button" variant="outline" size="sm"
              onClick={sendTestDigest}
              disabled={sendingTest}
              data-testid="digest-send-test-btn"
            >
              <Send className="w-4 h-4 mr-2" />
              {sendingTest ? 'Sending…' : 'Send Test Now'}
            </Button>
            <span className="text-xs text-slate-500">Preview opens in a new tab; test digest goes to your inbox.</span>
          </div>
        </CardContent>
      </Card>
      )}

      {/* Sub-user view: read-only note pointing them to their admin */}
      {!isMainUser && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-obsidian" /> Daily Fleet Health Digest
            </CardTitle>
            <CardDescription>
              Managed by your admin — ask them to switch it on for you from the Team page.
            </CardDescription>
          </CardHeader>
        </Card>
      )}
    </div>
  );
}
